/**
 * PopularCustomizationsTab - Analytics dashboard for popular customizations
 * Shows trending extras, modifiers, and revenue from upsells
 */
import { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Star, BarChart3, RefreshCw, Package, Flame, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function PopularCustomizationsTab() {
  const [analytics, setAnalytics] = useState(null);
  const [byItemData, setByItemData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchAnalytics(),
      fetchByItemData(),
      fetchRevenueData()
    ]);
    setLoading(false);
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Data refreshed!');
  };

  const fetchAnalytics = async () => {
    try {
      const res = await api.get('/admin/analytics/customizations');
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const fetchByItemData = async () => {
    try {
      const res = await api.get('/admin/analytics/customizations/by-item', { params: { limit: 15 } });
      setByItemData(res.data);
    } catch (err) {
      console.error('Failed to fetch by-item data:', err);
    }
  };

  const fetchRevenueData = async () => {
    try {
      const res = await api.get('/admin/analytics/customizations/revenue', { params: { days: 30 } });
      setRevenueData(res.data);
    } catch (err) {
      console.error('Failed to fetch revenue data:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="popular-customizations-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Popular Customizations</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Track what customers are ordering and optimize your menu
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshData}
          disabled={refreshing}
          className="rounded-full"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-sm text-muted-foreground">Total Tracked</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {analytics?.total_tracked_customizations || 0}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Unique customization types</p>
        </div>

        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-sm text-muted-foreground">Extras Revenue</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            ${(revenueData?.total_extras_revenue || 0).toFixed(2)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Last 30 days from paid extras</p>
        </div>

        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Star className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-sm text-muted-foreground">Top Extra</span>
          </div>
          <p className="text-lg font-bold text-foreground truncate">
            {analytics?.top_extras?.[0]?.customization_name || 'N/A'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {analytics?.top_extras?.[0]?.count || 0} orders
          </p>
        </div>

        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Flame className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="text-sm text-muted-foreground">Top Modifier</span>
          </div>
          <p className="text-lg font-bold text-foreground truncate">
            {analytics?.top_modifiers?.[0]?.customization_name || 'N/A'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {analytics?.top_modifiers?.[0]?.count || 0} orders
          </p>
        </div>
      </div>

      {/* Revenue Breakdown */}
      {revenueData?.top_revenue_extras?.length > 0 && (
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="font-heading text-lg text-foreground mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Top Revenue Extras (30 Days)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Extra</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Price</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Orders</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Revenue</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Items</th>
                </tr>
              </thead>
              <tbody>
                {revenueData.top_revenue_extras.slice(0, 10).map((extra, i) => (
                  <tr key={`extra-${extra.name}-${extra.revenue}`} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {i === 0 && <span className="text-amber-500">🥇</span>}
                        {i === 1 && <span className="text-gray-400">🥈</span>}
                        {i === 2 && <span className="text-amber-700">🥉</span>}
                        <span className="font-medium text-foreground">{extra.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">${extra.price.toFixed(2)}</td>
                    <td className="py-3 px-4 text-muted-foreground">{extra.orders}</td>
                    <td className="py-3 px-4">
                      <span className="font-medium text-green-600 dark:text-green-400">
                        ${extra.revenue.toFixed(2)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {extra.menu_items.slice(0, 2).map((item, j) => (
                          <span key={j} className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                            {item}
                          </span>
                        ))}
                        {extra.menu_items.length > 2 && (
                          <span className="text-xs text-muted-foreground">+{extra.menu_items.length - 2}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Customizations by Type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Extras */}
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="font-heading text-lg text-foreground mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-primary" />
            Top Extras
          </h3>
          <div className="space-y-3">
            {analytics?.top_extras?.slice(0, 5).map((item) => (
              <div key={`extra-${item.customization_name}`} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{item.customization_name}</span>
                  {item.price > 0 && (
                    <span className="text-xs text-green-600">+${item.price?.toFixed(2)}</span>
                  )}
                </div>
                <span className="text-sm text-muted-foreground">{item.count} orders</span>
              </div>
            )) || (
              <p className="text-sm text-muted-foreground text-center py-4">No data yet</p>
            )}
          </div>
        </div>

        {/* Top Modifiers */}
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="font-heading text-lg text-foreground mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            Top Modifiers
          </h3>
          <div className="space-y-3">
            {analytics?.top_modifiers?.slice(0, 5).map((item) => (
              <div key={`mod-${item.customization_name}`} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                <span className="text-sm font-medium text-foreground">{item.customization_name}</span>
                <span className="text-sm text-muted-foreground">{item.count} orders</span>
              </div>
            )) || (
              <p className="text-sm text-muted-foreground text-center py-4">No data yet</p>
            )}
          </div>
        </div>

        {/* Top Removed Ingredients */}
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="font-heading text-lg text-foreground mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-red-500" />
            Most Removed
          </h3>
          <div className="space-y-3">
            {analytics?.top_removed_ingredients?.slice(0, 5).map((item) => (
              <div key={`removed-${item.customization_name}`} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
                <span className="text-sm font-medium text-foreground">{item.customization_name}</span>
                <span className="text-sm text-muted-foreground">{item.count} orders</span>
              </div>
            )) || (
              <p className="text-sm text-muted-foreground text-center py-4">No data yet</p>
            )}
          </div>
          {analytics?.top_removed_ingredients?.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              Consider reviewing these ingredients in your recipes
            </p>
          )}
        </div>
      </div>

      {/* By Item Breakdown */}
      {byItemData?.items?.length > 0 && (
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="font-heading text-lg text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Customization Trends by Item
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Menu Item</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customized Orders</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Top Customizations</th>
                </tr>
              </thead>
              <tbody>
                {byItemData.items.slice(0, 10).map((item) => (
                  <tr key={`item-${item.item_name}`} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <span className="font-medium text-foreground">{item.item_name}</span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{item.total_customization_orders}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {item.top_customizations.slice(0, 3).map((cust, j) => (
                          <span 
                            key={j} 
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              cust.type === 'extra' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                              cust.type === 'modifier' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                              'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            }`}
                          >
                            {cust.name} ({cust.count})
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!analytics?.total_tracked_customizations && (
        <div className="bg-card rounded-2xl p-12 border border-border text-center">
          <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading text-lg text-foreground mb-2">No Customization Data Yet</h3>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            Customization analytics will appear here once customers start ordering items with extras, 
            modifiers, or removed ingredients. This data helps you understand what your customers prefer!
          </p>
        </div>
      )}
    </div>
  );
}
